import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box, Typography, Container, Alert, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

const AmbassadorOnboarding = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    college_id: '',
    college_name: '',
    college_address: ''
  });
  const [status, setStatus] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  /** 🔹 Contact dialog states */
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contact, setContact] = useState('');
  const [pendingSubmit, setPendingSubmit] = useState(false);

  useEffect(() => {
    const fetchAmbassadorStatus = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/ambassadors/check/`, { user_id: userId });
        setFormData({
          college_id: res.data.college_id || '',
          college_name: res.data.college_name || '',
          college_address: res.data.college_address || ''
        });
        setStatus(res.data.status);
        setEditMode(true);
      } catch (err) {
        setStatus(null); // No existing request
      }
    };

    fetchAmbassadorStatus();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    setAgreementChecked(e.target.checked);
  };

  const handleAgreementClick = (e) => {
    e.preventDefault();
    navigate('/campus-ambassador-agreement');
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  /** 🔹 Step 1: Before submit, check contact */
  const handleOpenDialog = async (e) => {
    e.preventDefault();

    try {
      const res = await axios.post(`${API_URL}/api/user/check-contact/`, {
        user_id: userId
      });

      if (!res.data.has_contact) {
        setPendingSubmit(true);
        setContactDialogOpen(true);
        return;
      }

      setDialogOpen(true);
    } catch {
      setErrorMsg('Unable to verify contact information.');
    }
  };

  /** 🔹 Save contact if missing */
  const handleSaveContact = async () => {
    if (!contact.trim()) return;

    try {
      await axios.post(`${API_URL}/api/user/update-contact/`, {
        user_id: userId,
        contact
      });

      setContactDialogOpen(false);

      if (pendingSubmit) {
        setPendingSubmit(false);
        setDialogOpen(true);
      }
    } catch {
      setErrorMsg('Failed to save contact.');
    }
  };

  /** 🔹 Step 2: Final submit/update */
  const handleSubmit = async () => {
    const payload = { ...formData, user_id: userId };

    try {
      if (editMode) {
        await axios.put(`${API_URL}/api/ambassadors/update/`, payload);
        setSuccessMsg('Ambassador request updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/ambassadors/create/`, payload);
        setSuccessMsg('Ambassador request submitted successfully!');
      }

      setErrorMsg('');
      setDialogOpen(false);
    } catch {
      setErrorMsg('Submission failed. Please try again.');
      setSuccessMsg('');
      setDialogOpen(false);
    }
  };

  return (
    <>
      <Header1 />
      <Container maxWidth="sm" sx={{ mt: 5, minHeight: '100vh', background: 'radial-gradient(circle at top, #0e0e16 0%, #000000 100%)', }}>

        <Typography
          variant="h3"
          component="h1"
          align="center"
          fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
          sx={{
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            mt: 10,
            letterSpacing: 7,
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
          gutterBottom
        >
          Join as Ambassador
        </Typography>

        {status === 'pending' && <Alert severity="info" sx={{ mb: 2 }}>Your request is under review.</Alert>}
        {status === 'approved' && <Alert severity="success" sx={{ mb: 2 }}>You are an approved Ambassador.</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

        <Box component="form" onSubmit={handleOpenDialog}>
          <TextField
            name="college_id"
            label="College ID"
            fullWidth
            required
            value={formData.college_id}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            name="college_name"
            label="College Name"
            fullWidth
            required
            value={formData.college_name}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            name="college_address"
            label="College Address"
            fullWidth
            multiline
            rows={3}
            required
            value={formData.college_address}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={agreementChecked}
                onChange={handleCheckboxChange}
                name="agreement"
                color="primary"
              />
            }
            label={
              <span>
                I agree to the{' '}
                <a
                  href="/campus-ambassador-agreement"
                  onClick={handleAgreementClick}
                  style={{ color: '#1976d2', textDecoration: 'underline', opacity: 0.8 }}
                >
                  Campus Ambassador Agreement
                </a>
              </span>
            }
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={!agreementChecked}
          >
            {editMode ? 'Update Request' : 'Submit Request'}
          </Button>
        </Box>

        {/* 🔹 Contact Dialog */}
        <Dialog open={contactDialogOpen}>
          <DialogTitle>Contact Required</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please provide your contact number to continue.
            </DialogContentText>
            <TextField
              fullWidth
              label="Contact Number"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveContact} variant="contained">
              Save & Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* 🔹 Confirmation Dialog */}
        <Dialog open={dialogOpen} onClose={handleDialogClose}>
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to {editMode ? 'update' : 'submit'} this Campus Ambassador request?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
      <Footer2 />
    </>
  );
};

export default AmbassadorOnboarding;
